import React, { Fragment } from "react";
import { Link } from "react-router-dom";
import "./Pathways.scss";
import NavBar from "../../shared/components/NavBar/NavBar";
import Footer from "../../shared/components/Footer/Footer";
import { cardData } from "../../shared/programCatalogData";
import {
  IUpcoming,
  approach,
  architecture,
  differentiators,
  stages,
  tracks,
} from "../../shared/pathways";

type Course = ReturnType<typeof cardData>[number];

// Shorter names so programs fit the pipeline cards.
const shortNames: Record<string, string> = {
  "/fundamentals-of-digital-system-design": "Fundamentals of Digital System Design",
  "/verilog-for-ASIC-FPGA-design-simulation": "{System}Verilog for ASIC/FPGA",
  "/hicar": "RISC-V Computer Architecture",
  "/system-on-a-chip-design-workshop": "SoC Design Workshop",
  "/analog-cmos-ic-design": "Art of Analog CMOS IC Design",
  "/cmos-ic-design": "CMOS Analog IC Design & Simulation",
  "/microcontroller-based-embedded-system-design": "Microcontroller-Based Embedded Systems",
  "/embedded-product-design": "Embedded Product Design for IoT",
  "/embedded-machine-learning-for-edge-computing": "Embedded ML for Edge Computing",
  "/introduction-to-generative-ai": "Intro to Generative AI",
  "/introduction-to-quantum-computing": "Intro to Quantum Computing",
  "/cryptography-fundamentals-securing-the-digital-world": "Cryptography Fundamentals",
};

const impact = [
  { icon: "bi-people-fill", value: "1,527+", label: "Learners enrolled" },
  { icon: "bi-star-fill", value: "4.85", label: "Average rating" },
  { icon: "bi-collection", value: "12", label: "Programs" },
  { icon: "bi-signpost-split", value: "6", label: "Specializations" },
  { icon: "bi-unlock", value: "4", label: "Free public workshops" },
  { icon: "bi-calendar-check", value: "2021", label: "Building since" },
];

const ProgramChip = ({ course, path }: { course?: Course; path: string }) => {
  const name = shortNames[path] ?? course?.title?.trim() ?? path;
  const isOpen = !!course?.currentState?.toLowerCase().includes("open");
  const isFree = !!course?.isOverlay && course?.overlayText === "Free";
  return (
    <Link to={path} className="pw-chip">
      <span className="pw-chip__name">{name}</span>
      <span className="pw-chip__meta">
        {course?.duration && <span>{course.duration}</span>}
        {isOpen && <span className="pw-badge pw-badge--open">Enrolling</span>}
        {isFree && <span className="pw-badge pw-badge--free">Free</span>}
        {course?.onDemand && <span className="pw-badge">On-demand</span>}
      </span>
    </Link>
  );
};

// An announced program without a page yet.
const UpcomingChip = ({ name, note }: IUpcoming) => (
  <div className="pw-chip pw-chip--upcoming">
    <span className="pw-chip__name">{name}</span>
    <span className="pw-chip__meta">
      <span className="pw-badge pw-badge--new">{note}</span>
    </span>
  </div>
);

const Pathways: React.FC = () => {
  const courses = cardData();
  const byPath = new Map<string, Course>(
    courses.map((c) => [c.actionProgramDetails as string, c])
  );
  const mapStages = stages.filter((s) => s.key !== "career");
  const career = stages.find((s) => s.key === "career")!;

  return (
    <div className="ss-pathways fade-in">
      <NavBar />

      <header className="pw-hero">
        <div className="pw-wrap">
          <span className="pw-eyebrow">Learning Pathways</span>
          <h1>
            From First Spark to <span>Industry&#8209;Ready</span>
          </h1>
          <p>
            An end&#8209;to&#8209;end learning pipeline that has taken 1,527+
            learners from free workshops to real silicon, working products and
            careers in chip design, embedded systems, AI and more.
          </p>
        </div>
      </header>

      {/* Pipeline */}
      <section className="pw-section" aria-labelledby="pw-pipeline-title">
        <div className="pw-wrap">
          <h2 id="pw-pipeline-title" className="visually-hidden">
            The learning pipeline
          </h2>
          <ol className="pw-steps" aria-hidden="true">
            {stages.map((stage) => (
              <li key={stage.key} style={{ color: stage.color }}>
                <i className={`bi ${stage.icon}`}></i>
                {stage.label}
              </li>
            ))}
          </ol>

          <ol className="pw-pipeline">
            {stages.map((stage, i) => (
              <li
                key={stage.key}
                className="pw-stage"
                style={{ "--stage": stage.color } as React.CSSProperties}
              >
                <div className="pw-stage__head">
                  <span className="pw-stage__num">0{i + 1}</span>
                  <h3>{stage.title}</h3>
                  <p>{stage.subtitle}</p>
                </div>
                <div className="pw-stage__body">
                  <span className="pw-stage__icon">
                    <i className={`bi ${stage.icon}`} aria-hidden="true"></i>
                  </span>
                  <ul className="pw-stage__points">
                    {stage.points.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                  {stage.programs && (
                    <div className="pw-stage__programs">
                      {stage.programs.map((path) => (
                        <ProgramChip key={path} path={path} course={byPath.get(path)} />
                      ))}
                      {stage.upcoming?.map((item) => (
                        <UpcomingChip key={item.name} {...item} />
                      ))}
                    </div>
                  )}
                  {stage.milestones && (
                    <ul className="pw-stage__milestones">
                      {stage.milestones.map((m) => (
                        <li key={m.text}>
                          <i className={`bi ${m.icon}`} aria-hidden="true"></i>
                          {m.text}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                {i < stages.length - 1 && (
                  <span className="pw-stage__arrow" aria-hidden="true">
                    <i className="bi bi-arrow-right"></i>
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Approach · Architecture · Quote */}
      <section className="pw-section pw-section--panels">
        <div className="pw-wrap pw-panels">
          <div className="pw-panel">
            <h2 className="pw-panel__title">Our Approach</h2>
            <ul className="pw-approach">
              {approach.map((item) => (
                <li key={item.text}>
                  <span>
                    <i className={`bi ${item.icon}`} aria-hidden="true"></i>
                  </span>
                  {item.text}
                </li>
              ))}
            </ul>
          </div>

          <div className="pw-panel pw-panel--wide">
            <h2 className="pw-panel__title">An Integrated Ecosystem</h2>
            <ol className="pw-arch">
              {architecture.map((item) => (
                <li key={item.name}>
                  <span className="pw-arch__icon">
                    <i className={`bi ${item.icon}`} aria-hidden="true"></i>
                  </span>
                  <strong>{item.verb}</strong>
                  <span className="pw-arch__name">{item.name}</span>
                  <span className="pw-arch__text">{item.text}</span>
                </li>
              ))}
            </ol>
            <div className="pw-diff">
              <h3>What Sets Us Apart</h3>
              <ul>
                {differentiators.map((item) => (
                  <li key={item.text}>
                    <i className={`bi ${item.icon}`} aria-hidden="true"></i>
                    {item.text}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <figure className="pw-panel pw-quote">
            <i className="bi bi-quote" aria-hidden="true"></i>
            <blockquote>
              I have been dreaming of studying at ENTC since I was a child, but
              I missed out. I am fulfilling that desire because of this
              program.
            </blockquote>
            <figcaption>Learner, Embedded Product Design for IoT &rsquo;25</figcaption>
          </figure>
        </div>
      </section>

      {/* Specialization map */}
      <section className="pw-section" aria-labelledby="pw-map-title">
        <div className="pw-wrap">
          <div className="pw-section__head">
            <span className="pw-eyebrow">Specialization Map</span>
            <h2 id="pw-map-title">
              Pick a Track, <span>Follow the Line</span>
            </h2>
            <p>
              Each specialization moves through the pipeline. Start where you
              are, and every track leads to being industry&#8209;ready.
            </p>
          </div>

          <div
            className="pw-map"
            style={{ "--cols": mapStages.length } as React.CSSProperties}
          >
            <div className="pw-map__corner" aria-hidden="true"></div>
            {mapStages.map((stage) => (
              <div
                key={stage.key}
                className="pw-map__stage"
                style={{ color: stage.color }}
              >
                <i className={`bi ${stage.icon}`} aria-hidden="true"></i>
                {stage.label}
              </div>
            ))}
            <div className="pw-map__stage pw-map__stage--career" style={{ color: career.color }}>
              <i className={`bi ${career.icon}`} aria-hidden="true"></i>
              {career.label}
            </div>

            {tracks.map((track, row) => {
              const cols = track.steps.map((step) =>
                mapStages.findIndex((s) => s.key === step.stage)
              );
              const first = Math.min(...cols);
              const gridRow = row + 2;
              return (
                <Fragment key={track.name}>
                  <Link
                    to={`/program-catalog?specialization=${track.name
                      .toLowerCase()
                      .replace(/[^a-z0-9]+/g, "-")}`}
                    className="pw-map__track"
                    style={{ gridRow, "--track": track.color } as React.CSSProperties}
                  >
                    <span>
                      <i className={`bi ${track.icon}`} aria-hidden="true"></i>
                    </span>
                    {track.name}
                  </Link>
                  <span
                    className="pw-map__line"
                    aria-hidden="true"
                    style={
                      {
                        gridRow,
                        gridColumn: `${first + 2} / -2`,
                        "--track": track.color,
                        // Cells the line crosses, so it can start mid-way through the first.
                        "--span": mapStages.length - first,
                      } as React.CSSProperties
                    }
                  ></span>
                  {track.steps.map((step) => {
                    const col = mapStages.findIndex((s) => s.key === step.stage);
                    return (
                      <div
                        key={step.stage}
                        className="pw-map__cell"
                        style={
                          {
                            gridRow,
                            gridColumn: col + 2,
                            "--track": track.color,
                          } as React.CSSProperties
                        }
                      >
                        <span className="pw-map__stage-tag">{mapStages[col].label}</span>
                        {step.programs?.map((path) => (
                          <ProgramChip key={path} path={path} course={byPath.get(path)} />
                        ))}
                        {step.upcoming?.map((item) => (
                          <UpcomingChip key={item.name} {...item} />
                        ))}
                        {step.outcome && (
                          <span className="pw-map__outcome">
                            <i className="bi bi-patch-check-fill" aria-hidden="true"></i>
                            {step.outcome}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </Fragment>
              );
            })}

            <div
              className="pw-map__career"
              style={{ gridRow: `2 / span ${tracks.length}`, "--track": career.color } as React.CSSProperties}
            >
              <i className="bi bi-briefcase" aria-hidden="true"></i>
              <strong>Industry &amp; life&#8209;ready</strong>
              <span>Soft skills, industry links and lifelong learning for every track</span>
            </div>
          </div>
        </div>
      </section>

      {/* Impact */}
      <section className="pw-section pw-section--impact">
        <div className="pw-wrap">
          <h2 className="pw-impact__title">The Impact So Far</h2>
          <ul className="pw-impact">
            {impact.map((item) => (
              <li key={item.label}>
                <i className={`bi ${item.icon}`} aria-hidden="true"></i>
                <span>
                  <strong>{item.value}</strong>
                  {item.label}
                </span>
              </li>
            ))}
          </ul>
          <p className="pw-tagline">
            From your first workshop to your first tapeout, and every step after.
          </p>
          <div className="pw-cta">
            <Link to="/program-catalog" className="ss-btn ss-btn--accent">
              Explore Programs <i className="bi bi-arrow-right" aria-hidden="true"></i>
            </Link>
            <Link to="/resources" className="ss-btn ss-btn--ghost-light">
              Free Resources
            </Link>
          </div>
        </div>
      </section>

      <Footer isFinancialInquiries={true} />
    </div>
  );
};

export default Pathways;
